import { useState } from 'react';

import { ErrorText, Field } from './ui';

import Button from '@/components/Button';
import Modal from '@/components/Modal';
import { lifecycleErrorMessage } from '@/services/lifecycleAPI';
import styled from '@/styles';

export const REASON_ERROR = 'Reason must be between 3 and 255 characters';

const Content = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
  padding: '18px 24px',
});

const Actions = styled('div', {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: '10px',
  padding: '14px 24px',
  borderTop: '1px solid #EDEDED',
});

const TextArea = styled('textarea', {
  font: 'inherit',
  fontSize: '14px',
  width: '100%',
  border: '1px solid #BDBDBD',
  borderRadius: '6px',
  padding: '8px 10px',
});

type Props = {
  title: string;
  body: string;
  confirmLabel: string;
  failureMessage: string;
  onSubmit: (reason: string) => Promise<void>;
  onClose: () => void;
};

const ReasonModal = ({
  title,
  body,
  confirmLabel,
  failureMessage,
  onSubmit,
  onClose,
}: Props) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3 || trimmed.length > 255) {
      setError(REASON_ERROR);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSubmit(trimmed);
    } catch (err) {
      setError(lifecycleErrorMessage(err, failureMessage));
      setSaving(false);
    }
  };

  return (
    <Modal
      disableClickout
      size="sm"
      title={title}
      onRequestClose={saving ? undefined : onClose}
    >
      <Content>
        <span>{body}</span>
        <Field htmlFor="journeyReasonInput">
          Reason
          <TextArea
            data-testid="journeyReasonInput"
            id="journeyReasonInput"
            rows={2}
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              setError(null);
            }}
          />
        </Field>
        {error && (
          <div role="alert">
            <ErrorText>{error}</ErrorText>
          </div>
        )}
      </Content>
      <Actions>
        <Button
          a11yLabel="Cancel"
          appearance="outline"
          disabled={saving}
          label="Cancel"
          onClick={onClose}
        />
        <Button
          a11yLabel={confirmLabel}
          appearance="primary"
          data-testid="journeyReasonConfirmBtn"
          isLoading={saving}
          label={confirmLabel}
          onClick={submit}
        />
      </Actions>
    </Modal>
  );
};

export default ReasonModal;
